// ─── src/api/elevate.ts ───────────────────────────────────────────────────────
//
// Admin API for the Startup Syria Elevate (Damascus 2026) mentor pool.
// All endpoints are x-api-key guarded on the backend.

import { apiClient } from "./registration";
import type { PaginationMeta } from "../types/registration";

export type ElevateAdminStatus =
  | "new_applicant"
  | "shortlisted"
  | "interview"
  | "accepted"
  | "rejected";

export interface ElevateApplication {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  gender: string;
  ageBracket: string;
  /** Legacy — collected on 2025 form, dropped on 2026 form. */
  currentStatus?: string | null;
  /** Legacy — collected on 2025 form, dropped on 2026 form. */
  skills?: string[] | null;
  governorate: string;
  city: string;
  yearsExperience: string;
  /** Legacy — replaced by `linkedinUrl` on the 2026 form. */
  linkedinOrBio?: string | null;
  /** Optional LinkedIn URL (2026 form). */
  linkedinUrl?: string | null;
  nominated: boolean;
  nominatingOrganisation?: string | null;
  expertiseAreas: string[];
  expertiseSectorOther?: string | null;
  expertiseOther?: string | null;
  motivation: string;
  learningAreas: string[];
  attendance: string;
  adminStatus: ElevateAdminStatus;
  adminNotes?: string | null;
  adminTags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ElevateParams {
  page?: number;
  limit?: number;
  search?: string;
  governorate?: string;
  attendance?: string;
  adminStatus?: ElevateAdminStatus;
}

export interface ElevateStats {
  total: number;
  byGovernorate: Record<string, number>;
  byAttendance: Record<string, number>;
  byAdminStatus: Record<string, number>;
}

export interface ElevateReviewPatch {
  adminStatus?: ElevateAdminStatus;
  adminNotes?: string | null;
  adminTags?: string[];
}

interface Paged<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface NormalisedList<T> {
  data: T[];
  meta: PaginationMeta;
}

const normalise = <T,>(raw: Paged<T>): NormalisedList<T> => ({
  data: raw.data,
  meta: {
    total: raw.total,
    page: raw.page,
    limit: raw.limit,
    totalPages: Math.ceil(raw.total / raw.limit),
  },
});

const clean = <T extends object>(params: T) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== "" && v !== null,
    ),
  );

export const getElevateApplications = async (
  params: ElevateParams,
): Promise<NormalisedList<ElevateApplication>> => {
  const { data } = await apiClient.get<Paged<ElevateApplication>>(
    "/elevate/applications",
    { params: clean(params) },
  );
  return normalise(data);
};

export const getElevateStats = async (): Promise<ElevateStats> => {
  const { data } = await apiClient.get<ElevateStats>(
    "/elevate/applications/stats",
  );
  return data;
};

export const reviewElevateApplication = async (
  id: string,
  patch: ElevateReviewPatch,
): Promise<ElevateApplication> => {
  const { data } = await apiClient.patch<ElevateApplication>(
    `/elevate/applications/${id}`,
    patch,
  );
  return data;
};

export const deleteElevateApplication = async (id: string): Promise<void> => {
  await apiClient.delete(`/elevate/applications/${id}`);
};

// ─── Participant feedback (Elevate Damascus 2026) ────────────────────────────

export type ElevateRating = 1 | 2 | 3 | 4 | 5;
export type ElevateDayKey = "day1" | "day2" | "day3";
export type ElevatePillarKey = "investing" | "ecosystems" | "network" | "cases" | "mentoring";
export type ElevateOrgAspect = "logistics" | "agenda" | "commsBefore" | "commsDuring";
export type ElevateDuration = "short" | "right" | "long";

export interface ElevateFeedback {
  id: string;
  createdAt: string;
  ratingOverall: number;
  /** 0–10. */
  nps: number;
  /** Optional. */
  keyTakeaway: string | null;
  /** null = did not attend that day. */
  dayRatings: Record<ElevateDayKey, number | null>;
  /** Up to 3 session keys. */
  topSessions: string[];
  weakSession: string | null;
  ratingSpeakers: number;
  /** Interaction, discussions and workshops, 1–5. */
  ratingInteraction: number | null;
  pillarRatings: Record<ElevatePillarKey, number>;
  /** The network of contacts they built, 1–5. */
  ratingNetworking: number;
  organizationRatings: Record<ElevateOrgAspect, number> | null;
  durationFit: ElevateDuration;
  /** Confidence mentoring a startup founder after the programme, 1–5. */
  mentorConfidence: number | null;
  supportNeeds: string | null;
  futureTopics: string | null;
  otherNotes: string | null;
  quoteConsent: boolean;
  /** Only set when quoteConsent is true. */
  quote: string | null;
  fullName: string | null;
  roleOrg: string | null;
}

/** Every participant feedback response, newest first. */
export const getElevateFeedback = async (): Promise<ElevateFeedback[]> => {
  const { data } = await apiClient.get<ElevateFeedback[]>("/elevate/feedback");
  return data;
};

/** Removes one response for good (test or spam submissions). */
export const deleteElevateFeedback = async (id: string): Promise<void> => {
  await apiClient.delete(`/elevate/feedback/${id}`);
};
